import type { FC, ReactNode } from 'react'

import { CookingPot, Soup, Wheat } from 'lucide-react'
import { SectionHeading } from '@/components/landing/section-heading'
import { freeToppings } from '@/constants/site'

const steps = [
    {
        icon: CookingPot,
        time: "10 PM",
        title: "The broth",
        text: "Whole free-range chickens go into the pot with smashed ginger, spring onions and white peppercorns. They simmer low all night until the broth turns gold."
    },
    {
        icon: Wheat,
        time: "4 AM",
        title: "The rice",
        text: "We rinse jasmine rice, crack the grains and stir them into the broth. Three hours of slow stirring turns them silky, never gluey."
    },
    {
        icon: Soup,
        time: "7 AM",
        title: "The finish",
        text: "Doors open. Every bowl is finished to order with hand-pulled chicken, ginger threads, scallions, crispy shallots and a drizzle of sesame oil."
    }
]

const stats = [
    { value: "6 hrs", label: "Broth simmering" },
    { value: "3 hrs", label: "Rice slowly stirred" },
    { value: String(freeToppings.length), label: "Toppings, always free" },
    { value: "5 min", label: "From order to bowl" }
]

export const ProcessSection: FC = (): ReactNode => {
    return (
        <section id='how-its-made' aria-labelledby='how-its-made-title' className='scroll-mt-20 py-20 sm:py-28'>
            <div className='mx-auto max-w-6xl px-5 sm:px-8'>
                <div className='grid gap-6 lg:grid-cols-[1fr_0.9fr] lg:items-end lg:gap-16'>
                    <SectionHeading
                        titleId='how-its-made-title'
                        eyebrow="How a bowl comes together"
                        title={<>Nine hours of patience in <em>every spoonful.</em></>}
                    />

                    <p className='text-lg leading-relaxed text-muted-foreground lg:pb-1'>
                        There are no shortcuts in our kitchen. Here is what happens long before you walk in, and why the first spoonful tastes the way it does.
                    </p>
                </div>

                <div className='relative mt-14'>
                    <span aria-hidden='true' className='absolute left-[16%] right-[16%] top-[3.25rem] hidden border-t-2 border-dashed border-primary/25 md:block' />

                    <ol className='relative grid gap-5 md:grid-cols-3'>
                        {steps.map((step, index) => (
                            <li
                                key={step.title}
                                className='group relative flex flex-col rounded-[1.75rem] border border-border bg-card p-7 shadow-sm shadow-broth/5 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-broth/10 motion-reduce:hover:translate-y-0'
                            >
                                <div className='flex items-center justify-between'>
                                    <span className='inline-flex size-12 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-md shadow-primary/25'>
                                        <step.icon className='size-6' aria-hidden='true' />
                                    </span>

                                    <span className='rounded-full bg-yolk-soft px-3 py-1 font-heading text-sm font-semibold italic text-foreground/80'>
                                        {step.time}
                                    </span>
                                </div>

                                <p className='mt-7 text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground'>
                                    Step {index + 1}
                                </p>

                                <h3 className='mt-1.5 text-2xl font-semibold'>
                                    {step.title}
                                </h3>

                                <p className='mt-3 leading-relaxed text-muted-foreground'>
                                    {step.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>

                <dl className='mt-10 grid grid-cols-2 gap-px overflow-hidden rounded-[1.75rem] border border-border bg-border sm:grid-cols-4'>
                    {stats.map(stat => (
                        <div
                            key={stat.label}
                            className='flex flex-col-reverse gap-1 bg-card p-6 sm:p-7'
                        >
                            <dt className='text-sm text-muted-foreground'>
                                {stat.label}
                            </dt>

                            <dd className='font-heading text-4xl font-semibold tracking-tight text-primary'>
                                {stat.value}
                            </dd>
                        </div>
                    ))}
                </dl>
            </div>
        </section>
    )
}
